"use client";

import { useState } from "react";
import { DeleteDialog } from "@/components/molecules/DeleteDialog";
import { FormProps, TreeItem } from "./types";
import { useForm2 } from "./hooks";
import {
  ParentComponentSection,
  TreeSection,
  FormSection,
  ParentComponentMessage,
} from "./components";

const Form2 = ({ selectedSection, onSave, pageId, pageComponentId, variantDetails }: FormProps) => {
  const {
    formState,
    currentVariantDetails,
    treeConfig,
    parentComponentFieldsConfig,
    fieldsConfig,
    handleParentComponentFieldsUpdate,
    handleComponentFieldsUpdate,
    handleParentComponentSave,
    handleSave,
    handleEdit,
    handleDelete,
    handleNewItem,
    toggleExpand,
  } = useForm2({ selectedSection, pageId, pageComponentId, variantDetails });

  // Delete confirmation state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TreeItem | null>(null);

  if (!currentVariantDetails || !treeConfig) {
    return null;
  }

  const showTreeSection =
    !treeConfig.requiresParentComponent ||
    selectedSection?.componentId ||
    formState.parentComponentId;

  const showParentComponentMessage =
    treeConfig.requiresParentComponent &&
    !selectedSection?.componentId &&
    !formState.parentComponentId &&
    treeConfig.parentComponentMessage;

  // Delete handlers with confirmation
  const handleDeleteWithConfirm = (item: TreeItem) => {
    setDeleteTarget(item);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget, onSave);
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const getDeleteConfirmation = (): string => {
    if (!deleteTarget || !treeConfig) return '';
    const titleField = treeConfig.fieldMappings?.title || 'title';
    const defaultMessage = `Delete "${deleteTarget[titleField]}"? This will also delete all child items.`;
    return treeConfig.deleteConfirmation?.(deleteTarget) ?? defaultMessage;
  };

  return (
    <div className="flex flex-col h-full">
      <ParentComponentSection
        formState={formState}
        treeConfig={treeConfig}
        parentComponentFieldsConfig={parentComponentFieldsConfig}
        onFieldsUpdate={handleParentComponentFieldsUpdate}
        onSave={handleParentComponentSave}
        onSaveCallback={onSave}
      />

      {showTreeSection && (
        <>
          <TreeSection
            formState={formState}
            treeConfig={treeConfig}
            onNewItem={handleNewItem}
            onToggleExpand={toggleExpand}
            onEdit={handleEdit}
            onDelete={(item, callback) => handleDeleteWithConfirm(item)}
            onSaveCallback={onSave}
          />

          <FormSection
            formState={formState}
            treeConfig={treeConfig}
            fieldsConfig={fieldsConfig}
            onFieldsUpdate={handleComponentFieldsUpdate}
            onSave={handleSave}
            onNewItem={handleNewItem}
            onSaveCallback={onSave}
          />
        </>
      )}

      {showParentComponentMessage && <ParentComponentMessage treeConfig={treeConfig} />}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && treeConfig && (
        <DeleteDialog
          open={isDeleteDialogOpen}
          title="Delete Item"
          itemName={deleteTarget[treeConfig.fieldMappings?.title || 'title']}
          description={getDeleteConfirmation()}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default Form2;
